
import Vue from 'vue';
import { RUNNIT_NODE_DEF_TOOL_APP_TYPE, RUNNIT_NODE_DEF_TOOL_TYPE, getRunnitNodeDefCostRange } from '@run-diffusion/shared';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';
import { TeamUserMixin } from '@/mixins';

export default Vue.extend({
	name: 'OfferingCard',
	props: {
		disabled: { type: Boolean, default: false },
		selected: { type: Boolean, default: false }, // Manually invoke hover state
		selectIcon: { type: String, default: 'mdi-check-circle-outline' },
		selectText: { type: String, default: 'Select' },
		selectedText: { type: String, default: 'Current' },
		showEditBtn: { type: Boolean, default: false },
		editText: { type: String, default: 'Edit' },
		disabledText: { type: String, default: null },
		disabledCtaText: { type: String, default: null },
		disabledCtaProps: { type: Object, default: () => ({}) },
		title: { type: String, default: '' },
		imageUrl: { type: String, default: '' },
		avatar: { type: [Object, String], default: null },
		description: { type: String, default: '' },
		tags: { type: Array, default: () => [] },
		horizontal: { type: Boolean, default: false },
		small: { type: Boolean, default: false },
		toolType: { type: String, required: false },
		isFeatured: { type: Boolean, required: false },
		noClick: { type: Boolean, default: false },
		fullImage: { type: Boolean, default: false },
		persistentFooter: { type: Boolean, default: false },
		imageOnly: { type: Boolean, default: false },
		imageHeightWidth: { type: Number, default: null }, // Explicit control of image dimensions
		clickableContainerClasses: { type: Object, default: null },
		runnitNodeDef: { type: Object, default: null },
	},
	data () {
		return {
			RUNNIT_NODE_DEF_TOOL_TYPE,
		};
	},
	mixins: [
		TeamUserMixin,
	],
	computed: {
		vertical () {
			return !this.horizontal && !this.fullImage;
		},
		computedContainerWidth (): number {
			let width: number = 300;

			if (this.imageOnly) {
				return this.computedImageHeightWidth;
			}

			if (this.vertical) {
				width = 300

				if (this.small) {
					width = 190;
				}
			}

			if (this.horizontal) {
				width = 460

				if (this.small) {
					width = 320;
				}
			}

			if (this.fullImage) {
				width = 256;
			}

			return width;
		},
		computedContainerHeight (): number {
			let height: number = 430;

			if (this.imageOnly) {
				return this.computedImageHeightWidth;
			}

			if (this.vertical) {
				height = 430

				if (this.small) {
					height = 300;
				}
			}

			if (this.horizontal) {
				height = 250

				if (this.small) {
					height = 150;
				}
			}

			if (this.fullImage) {
				height = 256;
			}

			return height;
		},
		computedImageHeightWidth (): number {
			// If imageHeightWidth is provided, use it
			if (this.imageHeightWidth) {
				return this.imageHeightWidth;
			}

			let heightWidth: number = 260;

			if (this.vertical) {
				heightWidth = 260

				if (this.small) {
					heightWidth = 150;
				}
			}

			if (this.horizontal) {
				heightWidth = 200

				if (this.small) {
					heightWidth = 120;
				}
			}

			if (this.fullImage) {
				heightWidth = 256;
			}

			return heightWidth;
		},
		computedDescriptionCharLimit (): number {
			const baseLimit: number = this.small ? 60 : 70;
			if (this.title && this.title.length > 18) {
				// Reduce character limit if title is long
				return baseLimit - 15; // Reduce by 15 characters
			}
			return baseLimit;
		},
		offeringCardStyles () {
			if (this.imageOnly) {
				return {
					background: 'transparent',
					boxShadow: 'none',
				};
			}

			let styles = {
				background: 'linear-gradient(180deg, var(--grey-825), var(--grey-835))',
			};

			if (this.toolType === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY) {
				styles.background = 'var(--blue-grey-800)';
				styles['backgroundImage'] = 'radial-gradient(circle at -30% 0, var(--blue-grey-500), rgba(255, 255, 255, 0) 70%)';
			}

			return styles;
		},
		descriptionStyles () {
			const styles = {
				color: 'rgba(255, 255, 255, 0.6)',
			};

			if (this.toolType === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY) {
				styles.color = 'var(--blue-grey-200)';
			}

			return styles;
		},
		generationLimitExceeded () {
			if (!this.runnitNodeDef) return false;
			if (this.runnitNodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER) return false; // Token limit does not apply to training
			if (!this.hasTokenLimitPermission) return false;
			const { minCostPerResult } = getRunnitNodeDefCostRange(
				this.runnitNodeDef.costPerResult,
				this.runnitNodeDef.costPerResultLogics,
				this.runnitNodeDef.fields,
			);
			const effectiveMinCost = minCostPerResult > 0 ? minCostPerResult : this.runnitNodeDef.costPerResult;
			// This tool's own cap: the role may cap its media type (Image, Video,
			// 3D, ...) differently from the master cap.
			const tokenLimitMax = this.getTokenLimitMaxForNodeDef(this.team, this.teamUserRole, this.runnitNodeDef);
			return effectiveMinCost > tokenLimitMax;
		},
	},
	methods: {
		onCardClick () {
			if (this.noClick || this.disabled) return;
			this.$emit('click');
		},
	},
	components: {
		RunnitImage,
	},
});
